import React, { useState, useMemo } from 'react';
import { Accordion, Row, Col, Table, Form, Card } from 'react-bootstrap';
import './faqQuestions.scss';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHeartPulse,
  faGraduationCap,
  faFileLines,
  faHouse,
  faMagnifyingGlass,
  faCircleInfo,
  faTriangleExclamation,
  faLock,
  faCoins,
  faLayerGroup,
  faCircleCheck,
  faPlus,
  faTrash,
  faReceipt,
  faCalendar,
  faArrowLeft,
  faArrowRight,
  faCheck,
  faXmark,
  faPercent,
  faBuildingColumns,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';
import StandardInput from 'base/StandardInput/StandardInput';
import { StandardCurrency } from 'base/StandardCurrency/StandardCurrency';
import { capitalizeFirstLetter, parseIntoNumber } from 'utils/helpers';
import {
  FaqIncomeTier,
  FaqProduct,
  FaqHealthInvoice,
  FaqEduEvent,
  FaqRefundPolicyType
} from '../../../types/interface';

// ─── Types ─────────────────────────────────────────────────────────────────────

type BasicData = 'with_income' | 'without_income';
type AppType = 'health' | 'education';

// ─── Helpers ───────────────────────────────────────────────────────────────────

const parseNum = (v: unknown): number => {
  const n = parseIntoNumber(String(v ?? ''));
  return n ?? 0;
};

const fmt = (v: number) =>
  v.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

// ─── Example Tiers ─────────────────────────────────────────────────────────────

const EXAMPLE_TIERS_WITH: FaqIncomeTier[] = [
  { net_income: 1000, refund_percentage: 80, support_provided: 200, maximum_support_provided: 600, threshold: 100 },
  { net_income: 1500, refund_percentage: 60, support_provided: 150, maximum_support_provided: 500, threshold: 100 },
  { net_income: 2000, refund_percentage: 40, support_provided: 100, maximum_support_provided: 400, threshold: 100 },
  { net_income: 2500, refund_percentage: 20, support_provided: 50,  maximum_support_provided: 300, threshold: 100 },
];

const EXAMPLE_TIERS_WITHOUT: FaqIncomeTier[] = [
  { net_income: 99999, refund_percentage: 100, support_provided: 300, maximum_support_provided: 600, threshold: 100 },
];

// ─── Calculation logic (mirrors useGenerateRefundPercentage.ts) ────────────────

function isProductRefundable(policy: FaqRefundPolicyType, svt: number): boolean {
  switch (policy) {
    case 'FULL_OPTIONAL_SV':                 return true;
    case 'CONDITIONAL_SV_ZERO_ALLOWED':      return svt >= 0;
    case 'CONDITIONAL_SV_REQUIRED_POSITIVE': return svt > 0;
    case 'NON_REFUNDABLE':                   return false;
    default:                                 return false;
  }
}

function calcHealthTotal(invoices: FaqHealthInvoice[]): number {
  let total = 0;
  invoices.forEach((inv) => {
    let refundableSum = 0;
    (inv.products || []).forEach((p: FaqProduct) => {
      if (isProductRefundable(p.refund_policy, parseNum(inv.svt))) {
        refundableSum += parseNum(p.amount);
      }
    });
    const net = refundableSum - parseNum(inv.svt);
    total += net < 0 ? 0 : net;
  });
  return total;
}

function calcEduTotal(events: FaqEduEvent[]): number {
  let total = 0;
  events.forEach((ev) => {
    if (ev.refund_policy === 'NON_REFUNDABLE') return;
    const template = parseIntoNumber(String(ev.filled_out_template_amount)) ?? 0;
    const payment  = parseIntoNumber(String(ev.payment_confirmation_amount)) ?? 0;
    const invoice  = parseIntoNumber(String(ev.invoice_amount)) ?? 0;
    let amount: number;
    if (template) {
      amount = template;
    } else if (ev.payment_confirmation_amount) {
      amount = payment;
    } else {
      amount = invoice + payment;
    }
    total += amount;
  });
  return total;
}

function matchTier(ipp: number, tiers: FaqIncomeTier[]): FaqIncomeTier | null {
  const sorted = [...tiers].sort((a, b) => parseNum(a.net_income) - parseNum(b.net_income));
  const match   = sorted.find((tier) => parseNum(tier.net_income) >= ipp);
  const closest = match ?? sorted[sorted.length - 1];
  if (!closest) return null;
  if (ipp > parseNum(closest.net_income)) return null; // exceeds last tier → 0%
  return closest;
}

interface FinalRefundResult {
  refund: number;
  calc: number;
  capped: boolean;
  floored: boolean;
}

function calcFinalRefund(
  total: number,
  basicData: BasicData,
  tier: FaqIncomeTier | null
): FinalRefundResult {
  if (!tier) return { refund: 0, calc: 0, capped: false, floored: false };

  if (basicData === 'without_income') {
    const refund = total >= parseNum(tier.threshold) ? parseNum(tier.support_provided) : 0;
    return { refund, calc: refund, capped: false, floored: false };
  }

  let calc    = (total * parseNum(tier.refund_percentage)) / 100;
  const maxS  = parseNum(tier.maximum_support_provided);
  const minS  = parseNum(tier.support_provided);
  const thr   = parseNum(tier.threshold);
  let capped  = false;
  let floored = false;

  if (calc > maxS)                  { calc = maxS;  capped  = true; }
  if (total >= thr && calc < minS)  { calc = minS;  floored = true; }

  return { refund: calc, calc, capped, floored };
}

// ─── Policy options (translated via helper render) ────────────────────────────

const POLICY_TYPES: FaqRefundPolicyType[] = [
  'FULL_OPTIONAL_SV',
  'CONDITIONAL_SV_ZERO_ALLOWED',
  'CONDITIONAL_SV_REQUIRED_POSITIVE',
  'NON_REFUNDABLE',
];

const POLICY_BADGE: Record<FaqRefundPolicyType, string> = {
  FULL_OPTIONAL_SV:                 'success',
  CONDITIONAL_SV_ZERO_ALLOWED:      'warning',
  CONDITIONAL_SV_REQUIRED_POSITIVE: 'warning',
  NON_REFUNDABLE:                   'danger',
};

// ─── Small UI helpers ──────────────────────────────────────────────────────────

const FaqNote: React.FC<{ type?: 'info' | 'warning'; children: React.ReactNode; className?: string }> = ({
  type = 'info',
  children,
  className = '',
}) => (
  <div className={`faq-note faq-note--${type} ${className}`}>
    <FontAwesomeIcon
      icon={type === 'warning' ? faTriangleExclamation : faCircleInfo}
      className="faq-note__icon"
    />
    <span>{children}</span>
  </div>
);

const FormulaBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="faq-formula-box">{children}</div>
);

// ─── Step Navigator ────────────────────────────────────────────────────────────

interface StepNavProps {
  step: number;
  labels: string[];
}

const StepNav: React.FC<StepNavProps> = ({ step, labels }) => (
  <div className="faq-step-nav">
    {labels.map((lbl, i) => (
      <React.Fragment key={i}>
        <div className={`faq-step-item${i < step ? ' done' : ''}${i === step ? ' active' : ''}`}>
          <div className="faq-step-circle">
            {i < step ? (
              <FontAwesomeIcon icon={faCheck} className="fs-12" />
            ) : (
              <span className="fs-12 fw-semibold">{i + 1}</span>
            )}
          </div>
          <div className="faq-step-lbl">{lbl}</div>
        </div>
        {i < labels.length - 1 && <div className="faq-step-connector" />}
      </React.Fragment>
    ))}
  </div>
);

// ─── Calculator Step Components ────────────────────────────────────────────────

interface Step1Props {
  totalIncome: number;
  setTotalIncome: (v: number) => void;
  persons: number;
  setPersons: (v: number) => void;
  ipp: number;
}

const Step1: React.FC<Step1Props> = ({ totalIncome, setTotalIncome, persons, setPersons, ipp }) => {
  const { t } = useTranslation();
  return (
    <div className="faq-step-panel">
      <Row className="g-3 mb-3">
        <Col md={6}>
          <div className="calc-input-card">
            <StandardCurrency
              label={capitalizeFirstLetter(t('faq.calc.total_income_label'))}
              value={totalIncome || ''}
              onChange={(v) => setTotalIncome(v ?? 0)}
              validations={[]}
              showError={false}
              className="mb-0"
            />
            <span className="calc-input-hint">{capitalizeFirstLetter(t('faq.calc.total_income_hint'))}</span>
          </div>
        </Col>
        <Col md={6}>
          <div className="calc-input-card">
            <StandardInput
              label={capitalizeFirstLetter(t('faq.calc.persons_label'))}
              value={persons || ''}
              onChange={(v) => setPersons(Math.max(1, parseInt(String(v)) || 1))}
              validations={[]}
              showError={false}
              placeholder=""
              className=""
              type="number"
            />
            <span className="calc-input-hint">{capitalizeFirstLetter(t('faq.calc.persons_hint'))}</span>
          </div>
        </Col>
      </Row>

      <div className="calc-ipp-card">
        <div>
          <div className="calc-ipp-card__formula">{capitalizeFirstLetter(t('faq.calc.ipp_formula'))}</div>
          <div className="calc-ipp-card__label">
            <FontAwesomeIcon icon={faCoins} className="me-2 text-danger" />
            {capitalizeFirstLetter(t('faq.calc.ipp_label'))}
          </div>
        </div>
        <div className="calc-ipp-card__value">{fmt(ipp)}</div>
      </div>

      <FaqNote>{capitalizeFirstLetter(t('faq.calc.step1_note'))}</FaqNote>
    </div>
  );
};

interface Step2Props {
  ipp: number;
  basicData: BasicData;
  setBasicData: (v: BasicData) => void;
  activeTier: FaqIncomeTier | null;
  tiers: FaqIncomeTier[];
}

const Step2: React.FC<Step2Props> = ({ ipp, basicData, setBasicData, activeTier, tiers }) => {
  const { t } = useTranslation();
  return (
    <div className="faq-step-panel">
      <p className="fs-14">{capitalizeFirstLetter(t('faq.calc.step2_desc1'))}</p>

      <div className="d-flex gap-2 mb-3 flex-wrap">
        {(['with_income', 'without_income'] as BasicData[]).map((bd) => (
          <button
            key={bd}
            type="button"
            className={`faq-type-card${basicData === bd ? ' selected' : ''}`}
            onClick={() => setBasicData(bd)}
          >
            <span className="faq-tc-icon">
              <FontAwesomeIcon icon={bd === 'with_income' ? faCoins : faBuildingColumns} />
            </span>
            <span className="fs-13 fw-semibold">
              {capitalizeFirstLetter(t(`faq.calc.step3_${bd}`))}
            </span>
            {basicData === bd && (
              <span className="faq-sel-badge">
                <FontAwesomeIcon icon={faCheck} />
              </span>
            )}
          </button>
        ))}
      </div>

      {basicData === 'with_income' ? (
        <>
          <p className="fs-13">{capitalizeFirstLetter(t('faq.calc.step2_desc2'))}</p>
          <Table bordered size="sm" className="faq-tier-table fs-13">
            <thead className="table-light">
              <tr>
                <th>{capitalizeFirstLetter(t('faq.calc.step2_net_income'))}</th>
                <th>{capitalizeFirstLetter(t('faq.calc.step2_pct'))}</th>
                <th>{capitalizeFirstLetter(t('faq.calc.step2_min'))}</th>
                <th>{capitalizeFirstLetter(t('faq.calc.step2_max'))}</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier, i) => {
                const isActive = activeTier === tier;
                return (
                  <tr key={i} className={isActive ? 'hl' : ''}>
                    <td>≤ {fmt(parseNum(tier.net_income))}</td>
                    <td>
                      <strong>{tier.refund_percentage}%</strong>
                    </td>
                    <td>{fmt(parseNum(tier.support_provided))}</td>
                    <td>{fmt(parseNum(tier.maximum_support_provided))}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          {!activeTier && ipp > 0 && (
            <FaqNote type="warning">{capitalizeFirstLetter(t('faq.calc.step2_exceed'))}</FaqNote>
          )}
          {activeTier && (
            <FormulaBox>
              <span className="fs-13">{capitalizeFirstLetter(t('faq.calc.step2_tier_col'))}: </span>
              <strong>{activeTier.refund_percentage}%</strong>
              <span className="faq-result-chip ms-3">
                <FontAwesomeIcon icon={faPercent} className="me-1" />
                {fmt(ipp)}
              </span>
            </FormulaBox>
          )}
        </>
      ) : (
        <FaqNote>{capitalizeFirstLetter(t('faq.calc.step2_without_income_note'))}</FaqNote>
      )}
    </div>
  );
};

// ─── Health invoice form ───────────────────────────────────────────────────────

interface HealthInvoiceFormProps {
  invoices: FaqHealthInvoice[];
  onChange: (invoices: FaqHealthInvoice[]) => void;
}

const emptyProduct = (): FaqProduct => ({ name: '', amount: 0, refund_policy: 'FULL_OPTIONAL_SV' });
const emptyInvoice = (): FaqHealthInvoice => ({ products: [emptyProduct()], svt: 0 });

const HealthInvoiceForm: React.FC<HealthInvoiceFormProps> = ({ invoices, onChange }) => {
  const { t } = useTranslation();

  const updateInvoice = (idx: number, upd: Partial<FaqHealthInvoice>) => {
    const next = invoices.map((inv, i) => (i === idx ? { ...inv, ...upd } : inv));
    onChange(next);
  };

  const addProduct = (invIdx: number) => {
    const next = invoices.map((inv, i) =>
      i === invIdx ? { ...inv, products: [...inv.products, emptyProduct()] } : inv
    );
    onChange(next);
  };

  const updateProduct = (invIdx: number, prodIdx: number, upd: Partial<FaqProduct>) => {
    const next = invoices.map((inv, i) => {
      if (i !== invIdx) return inv;
      return {
        ...inv,
        products: inv.products.map((p: FaqProduct, j: number) => (j === prodIdx ? { ...p, ...upd } : p)),
      };
    });
    onChange(next);
  };

  const removeProduct = (invIdx: number, prodIdx: number) => {
    const next = invoices.map((inv, i) => {
      if (i !== invIdx) return inv;
      return { ...inv, products: inv.products.filter((_: FaqProduct, j: number) => j !== prodIdx) };
    });
    onChange(next);
  };

  const policyOptions = POLICY_TYPES.map((p: string) => ({
    value: p,
    label: String(t(`faq.calc.policy_${p.toLowerCase()}`, p)),
  }));

  return (
    <div className="d-flex flex-column gap-3">
      {invoices.map((inv, invIdx) => {
        const svt = parseNum(inv.svt);
        let refundableSum = 0;
        inv.products.forEach((p: FaqProduct) => {
          if (isProductRefundable(p.refund_policy, svt)) refundableSum += parseNum(p.amount);
        });
        const invoiceNet = Math.max(0, refundableSum - svt);

        return (
          <div key={invIdx} className="faq-invoice-card p-3 rounded border">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <strong className="fs-14">
                <FontAwesomeIcon icon={faReceipt} className="me-2 text-danger" />
                {capitalizeFirstLetter(t('faq.calc.step3_invoice'))} {invIdx + 1}
              </strong>
            </div>

            {inv.products.map((prod, prodIdx) => (
              <Row key={prodIdx} className="g-2 mb-2 align-items-end">
                <Col xs={12} sm={4}>
                  <StandardInput
                    label={prodIdx === 0 ? capitalizeFirstLetter(t('faq.calc.step3_product_name')) : ''}
                    value={prod.name}
                    onChange={(v) => updateProduct(invIdx, prodIdx, { name: String(v) })}
                    validations={[]}
                    showError={false}
                    placeholder={t('faq.calc.step3_product_name')}
                    className=""
                    type="text"
                  />
                </Col>
                <Col xs={12} sm={3}>
                  <StandardCurrency
                    label={prodIdx === 0 ? capitalizeFirstLetter(t('faq.calc.step3_product_amount')) : ''}
                    value={prod.amount || ''}
                    onChange={(v) => updateProduct(invIdx, prodIdx, { amount: v ?? 0 })}
                    validations={[]}
                    showError={false}
                    className="mb-0"
                  />
                </Col>
                <Col xs={12} sm={4}>
                  <Form.Label className="fs-13 mb-1" style={{ display: prodIdx === 0 ? 'block' : 'none' }}>
                    {capitalizeFirstLetter(t('faq.calc.step3_refund_policy'))}
                  </Form.Label>
                  <Form.Select
                    size="sm"
                    value={prod.refund_policy}
                    onChange={(e) =>
                      updateProduct(invIdx, prodIdx, { refund_policy: e.target.value as FaqRefundPolicyType })
                    }
                  >
                    {policyOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12} sm={1} className="d-flex align-items-end pb-1">
                  {inv.products.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-link text-danger p-0 ms-1"
                      onClick={() => removeProduct(invIdx, prodIdx)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </Col>
              </Row>
            ))}

            <button
              type="button"
              className="btn btn-sm btn-outline-danger mb-3"
              onClick={() => addProduct(invIdx)}
            >
              <FontAwesomeIcon icon={faPlus} className="me-1" />
              {capitalizeFirstLetter(t('faq.calc.step3_add_product'))}
            </button>

            <Row className="g-2 align-items-end">
              <Col md={5}>
                <StandardCurrency
                  label={capitalizeFirstLetter(t('faq.calc.step3_svt'))}
                  value={inv.svt || ''}
                  onChange={(v) => updateInvoice(invIdx, { svt: v ?? 0 })}
                  validations={[]}
                  showError={false}
                  className="mb-0"
                />
                <small className="text-muted">{capitalizeFirstLetter(t('faq.calc.step3_svt_note'))}</small>
              </Col>
            </Row>

            <div className="faq-brow mt-2">
              <span className="fs-13">{capitalizeFirstLetter(t('faq.calc.step3_subtotal'))}:</span>
              <strong className="ms-auto">{fmt(invoiceNet)}</strong>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="btn btn-sm btn-outline-danger align-self-start"
        onClick={() => onChange([...invoices, emptyInvoice()])}
      >
        <FontAwesomeIcon icon={faPlus} className="me-1" />
        {capitalizeFirstLetter(t('faq.calc.step3_add_invoice'))}
      </button>
    </div>
  );
};

// ─── Education event form ─────────────────────────────────────────────────────

interface EduEventFormProps {
  events: FaqEduEvent[];
  onChange: (events: FaqEduEvent[]) => void;
}

const emptyEduEvent = (): FaqEduEvent => ({
  refund_policy: 'FULL_OPTIONAL_SV',
  invoice_amount: 0,
  payment_confirmation_amount: 0,
  filled_out_template_amount: 0,
});

const EduEventForm: React.FC<EduEventFormProps> = ({ events, onChange }) => {
  const { t } = useTranslation();

  const updateEvent = (idx: number, upd: Partial<FaqEduEvent>) => {
    onChange(events.map((ev, i) => (i === idx ? { ...ev, ...upd } : ev)));
  };

  const policyOptions = POLICY_TYPES.map((p: string) => ({
    value: p,
    label: String(t(`faq.calc.policy_${p.toLowerCase()}`, p)),
  }));

  return (
    <div className="d-flex flex-column gap-3">
      {events.map((ev, i) => {
        const template = parseIntoNumber(String(ev.filled_out_template_amount)) ?? 0;
        const payment  = parseIntoNumber(String(ev.payment_confirmation_amount)) ?? 0;
        const invoice  = parseIntoNumber(String(ev.invoice_amount)) ?? 0;
        let amountUsed = 0;
        let sourceLabel = '';

        if (ev.refund_policy === 'NON_REFUNDABLE') {
          sourceLabel = capitalizeFirstLetter(t('faq.calc.step3_not_refundable'));
        } else if (template) {
          amountUsed  = template;
          sourceLabel = capitalizeFirstLetter(t('faq.calc.step3_source_template'));
        } else if (ev.payment_confirmation_amount) {
          amountUsed  = payment;
          sourceLabel = capitalizeFirstLetter(t('faq.calc.step3_source_payment'));
        } else {
          amountUsed  = invoice + payment;
          sourceLabel = capitalizeFirstLetter(t('faq.calc.step3_source_invoice_plus'));
        }

        return (
          <div key={i} className="faq-invoice-card p-3 rounded border">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <strong className="fs-14">
                <FontAwesomeIcon icon={faCalendar} className="me-2 text-danger" />
                {capitalizeFirstLetter(t('faq.calc.step3_event'))} {i + 1}
              </strong>
              {events.length > 1 && (
                <button
                  type="button"
                  className="btn btn-link text-danger p-0"
                  onClick={() => onChange(events.filter((_, j) => j !== i))}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              )}
            </div>

            <Row className="g-2 mb-2">
              <Col md={4}>
                <Form.Label className="fs-13 mb-1">
                  {capitalizeFirstLetter(t('faq.calc.step3_refund_policy'))}
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={ev.refund_policy}
                  onChange={(e) => updateEvent(i, { refund_policy: e.target.value as FaqRefundPolicyType })}
                >
                  {policyOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={4}>
                <StandardCurrency
                  label={capitalizeFirstLetter(t('faq.calc.step3_invoice_amount'))}
                  value={ev.invoice_amount || ''}
                  onChange={(v) => updateEvent(i, { invoice_amount: v ?? 0 })}
                  validations={[]}
                  showError={false}
                  className="mb-0"
                  disabled={ev.refund_policy === 'NON_REFUNDABLE'}
                />
              </Col>
              <Col md={4}>
                <StandardCurrency
                  label={capitalizeFirstLetter(t('faq.calc.step3_payment_conf'))}
                  value={ev.payment_confirmation_amount || ''}
                  onChange={(v) => updateEvent(i, { payment_confirmation_amount: v ?? 0 })}
                  validations={[]}
                  showError={false}
                  className="mb-0"
                  disabled={ev.refund_policy === 'NON_REFUNDABLE'}
                />
              </Col>
              <Col md={4}>
                <StandardCurrency
                  label={capitalizeFirstLetter(t('faq.calc.step3_template_amount'))}
                  value={ev.filled_out_template_amount || ''}
                  onChange={(v) => updateEvent(i, { filled_out_template_amount: v ?? 0 })}
                  validations={[]}
                  showError={false}
                  className="mb-0"
                  disabled={ev.refund_policy === 'NON_REFUNDABLE'}
                />
              </Col>
            </Row>

            <div className="faq-brow">
              <span className="fs-13">{capitalizeFirstLetter(t('faq.calc.step3_amount_used'))}: <em>{sourceLabel}</em></span>
              <strong className={`ms-auto ${ev.refund_policy === 'NON_REFUNDABLE' ? 'text-muted' : 'fw-bold'}`}>
                {ev.refund_policy === 'NON_REFUNDABLE' ? fmt(0) : fmt(amountUsed)}
              </strong>
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className="btn btn-sm btn-outline-danger align-self-start"
        onClick={() => onChange([...events, emptyEduEvent()])}
      >
        <FontAwesomeIcon icon={faPlus} className="me-1" />
        {capitalizeFirstLetter(t('faq.calc.step3_add_invoice'))}
      </button>
    </div>
  );
};

// ─── Step 3 ───────────────────────────────────────────────────────────────────

interface Step3Props {
  appType: AppType;
  setAppType: (v: AppType) => void;
  invoices: FaqHealthInvoice[];
  setInvoices: (v: FaqHealthInvoice[]) => void;
  events: FaqEduEvent[];
  setEvents: (v: FaqEduEvent[]) => void;
  total: number;
}

const Step3: React.FC<Step3Props> = ({
  appType, setAppType, invoices, setInvoices, events, setEvents, total,
}) => {
  const { t } = useTranslation();
  return (
    <div className="faq-step-panel">
      <p className="fs-14">{capitalizeFirstLetter(t('faq.calc.step3_desc'))}</p>

      <Row className="g-3 mb-4">
        {(['health', 'education'] as AppType[]).map((type) => (
          <Col key={type} xs={6}>
            <div
              className={`faq-type-card w-100${appType === type ? ' selected' : ''}`}
              onClick={() => setAppType(type)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setAppType(type)}
            >
              <span className="faq-tc-icon">
                <FontAwesomeIcon icon={type === 'health' ? faHeartPulse : faGraduationCap} />
              </span>
              <div>
                <div className="fw-semibold fs-14">
                  {capitalizeFirstLetter(t(`faq.calc.step3_${type}`))}
                </div>
                <div className="text-muted fs-12">
                  {capitalizeFirstLetter(t(`faq.calc.step3_${type}_sub`))}
                </div>
              </div>
              {appType === type && (
                <span className="faq-sel-badge">
                  <FontAwesomeIcon icon={faCheck} />
                </span>
              )}
            </div>
          </Col>
        ))}
      </Row>

      {appType === 'health' ? (
        <HealthInvoiceForm invoices={invoices} onChange={setInvoices} />
      ) : (
        <EduEventForm events={events} onChange={setEvents} />
      )}

      <FormulaBox>
        <div className="faq-brow">
          <span className="fs-13">{capitalizeFirstLetter(t('faq.calc.step4_total_invoice'))}</span>
          <strong className="text-danger ms-auto fs-15">{fmt(total)}</strong>
        </div>
      </FormulaBox>
    </div>
  );
};

// ─── Step 4 ───────────────────────────────────────────────────────────────────

interface Step4Props {
  total: number;
  basicData: BasicData;
  activeTier: FaqIncomeTier | null;
  result: FinalRefundResult;
}

const Step4: React.FC<Step4Props> = ({ total, basicData, activeTier, result }) => {
  const { t } = useTranslation();

  if (total === 0) {
    return (
      <div className="faq-step-panel">
        <FaqNote type="warning">{capitalizeFirstLetter(t('faq.calc.step4_complete_step3'))}</FaqNote>
      </div>
    );
  }

  const tier = activeTier;

  return (
    <div className="faq-step-panel">
      {basicData === 'with_income' ? (
        <>
          <FormulaBox>
            <div className="fw-semibold mb-1">{capitalizeFirstLetter(t('faq.calc.step4_formula_a'))}</div>
            <code className="fs-13">
              {fmt(total)} × {tier?.refund_percentage ?? 0}% = {fmt(total * (tier?.refund_percentage ?? 0) / 100)}
            </code>
          </FormulaBox>

          {result.capped && (
            <FormulaBox>
              <div className="fw-semibold mb-1">{capitalizeFirstLetter(t('faq.calc.step4_formula_b'))}</div>
              <code className="fs-13">
                {fmt(total * (tier?.refund_percentage ?? 0) / 100)} &gt; {fmt(parseNum(tier?.maximum_support_provided))} → {fmt(parseNum(tier?.maximum_support_provided))}
              </code>
            </FormulaBox>
          )}

          {result.floored && (
            <FormulaBox>
              <div className="fw-semibold mb-1">{capitalizeFirstLetter(t('faq.calc.step4_formula_c'))}</div>
              <code className="fs-13">
                {fmt(total)} ≥ {fmt(parseNum(tier?.threshold))} → min {fmt(parseNum(tier?.support_provided))}
              </code>
            </FormulaBox>
          )}

          <div className="faq-breakdown mt-3">
            <div className="faq-brow">
              <span>{capitalizeFirstLetter(t('faq.calc.step4_total_invoice'))}</span>
              <span>{fmt(total)}</span>
            </div>
            <div className="faq-brow">
              <span>{capitalizeFirstLetter(t('faq.calc.step4_refund_pct'))}</span>
              <span className="fw-bold">{tier?.refund_percentage ?? 0}%</span>
            </div>
            <div className="faq-brow">
              <span>{capitalizeFirstLetter(t('faq.calc.step4_calculated'))}</span>
              <span>{fmt(total * (tier?.refund_percentage ?? 0) / 100)}</span>
            </div>
            {result.capped && (
              <div className="faq-brow">
                <span>{capitalizeFirstLetter(t('faq.calc.step4_max_cap'))}</span>
                <span>{fmt(parseNum(tier?.maximum_support_provided))}</span>
              </div>
            )}
            {result.floored && (
              <div className="faq-brow">
                <span>{capitalizeFirstLetter(t('faq.calc.step4_min_floor'))}</span>
                <span>{fmt(parseNum(tier?.support_provided))}</span>
              </div>
            )}
            <div className="faq-brow faq-brow--total">
              <span className="fw-bold">{capitalizeFirstLetter(t('faq.calc.step4_final'))}</span>
              <span className="faq-result-chip fs-15">
                <FontAwesomeIcon icon={faCoins} className="me-1" />
                {fmt(result.refund)}
              </span>
            </div>
          </div>
        </>
      ) : (
        <>
          <FormulaBox>
            <div className="fw-semibold mb-1">
              {capitalizeFirstLetter(t('faq.calc.step4_without_income_check'))}
            </div>
            <code className="fs-13">
              {fmt(total)} {total >= parseNum(tier?.threshold) ? '≥' : '<'} {fmt(parseNum(tier?.threshold))} →{' '}
              {total >= parseNum(tier?.threshold)
                ? fmt(parseNum(tier?.support_provided))
                : fmt(0)}
            </code>
          </FormulaBox>

          <div className="faq-breakdown mt-3">
            <div className="faq-brow">
              <span>{capitalizeFirstLetter(t('faq.calc.step4_total_invoice'))}</span>
              <span>{fmt(total)}</span>
            </div>
            <div className="faq-brow">
              <span>{capitalizeFirstLetter(t('faq.calc.step4_threshold'))}</span>
              <span>{fmt(parseNum(tier?.threshold))}</span>
            </div>
            <div className="faq-brow faq-brow--total">
              <span className="fw-bold">{capitalizeFirstLetter(t('faq.calc.step4_final'))}</span>
              <span className="faq-result-chip fs-15">
                <FontAwesomeIcon icon={faCoins} className="me-1" />
                {fmt(result.refund)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Step 5 ───────────────────────────────────────────────────────────────────

const Step5: React.FC<{ tier: FaqIncomeTier | null; appType: AppType }> = ({ tier, appType }) => {
  const { t } = useTranslation();
  return (
    <div className="faq-step-panel">
      <p className="fs-14">{capitalizeFirstLetter(t('faq.calc.step5_desc'))}</p>

      <Row className="g-3">
        <Col md={6}>
          <div className="faq-cap-card h-100">
            <FontAwesomeIcon icon={appType === 'health' ? faHeartPulse : faGraduationCap} className="text-danger mb-2 fs-20" />
            <div className="text-muted fs-12 mb-1">{capitalizeFirstLetter(t('faq.calc.step5_max_per_app'))}</div>
            <strong className="text-danger fs-16">{fmt(parseNum(tier?.maximum_support_provided))}</strong>
          </div>
        </Col>
        <Col md={6}>
          <div className="faq-cap-card h-100">
            <FontAwesomeIcon icon={faChartLine} className="text-danger mb-2 fs-20" />
            <div className="text-muted fs-12 mb-1">{capitalizeFirstLetter(t('faq.calc.step5_min_support'))}</div>
            <strong className="text-danger fs-16">{fmt(parseNum(tier?.support_provided))}</strong>
          </div>
        </Col>
        <Col md={6}>
          <div className="faq-cap-card h-100">
            <FontAwesomeIcon icon={faCoins} className="text-danger mb-2 fs-20" />
            <div className="text-muted fs-12 mb-1">{capitalizeFirstLetter(t('faq.calc.step5_threshold_label'))}</div>
            <strong className="text-danger fs-16">{fmt(parseNum(tier?.threshold))}</strong>
          </div>
        </Col>
      </Row>

      <FaqNote className="mt-3">{capitalizeFirstLetter(t('faq.calc.step5_note'))}</FaqNote>
    </div>
  );
};

// ─── Refund Calculator (main wrapper) ────────────────────────────────────────

const RefundCalculator: React.FC = () => {
  const { t } = useTranslation();
  const [step, setStep]           = useState(0);
  const [appType, setAppType]     = useState<AppType>('health');
  const [basicData, setBasicData] = useState<BasicData>('with_income');
  const [totalIncome, setTotalIncome] = useState(0);
  const [persons, setPersons]     = useState(1);
  const [invoices, setInvoices]   = useState<FaqHealthInvoice[]>([emptyInvoice()]);
  const [events, setEvents]       = useState<FaqEduEvent[]>([emptyEduEvent()]);

  const tiers = basicData === 'with_income' ? EXAMPLE_TIERS_WITH : EXAMPLE_TIERS_WITHOUT;

  const ipp = useMemo(
    () => (persons > 0 ? totalIncome / persons : 0),
    [totalIncome, persons]
  );

  const activeTier = useMemo(
    () => (basicData === 'with_income' ? matchTier(ipp, tiers) : tiers[0]),
    [ipp, tiers, basicData]
  );

  const invoiceTotal = useMemo(
    () => (appType === 'health' ? calcHealthTotal(invoices) : calcEduTotal(events)),
    [appType, invoices, events]
  );

  const result = useMemo(
    () => calcFinalRefund(invoiceTotal, basicData, activeTier),
    [invoiceTotal, basicData, activeTier]
  );

  const STEP_LABELS = [
    capitalizeFirstLetter(t('faq.calc.step1_title')),
    capitalizeFirstLetter(t('faq.calc.step2_title')),
    capitalizeFirstLetter(t('faq.calc.step3_title')),
    capitalizeFirstLetter(t('faq.calc.step4_title')),
    capitalizeFirstLetter(t('faq.calc.step5_title')),
  ];

  return (
    <div className="faq-calculator">
      <StepNav step={step} labels={STEP_LABELS} />

      <div className="faq-calc-body mt-4">
        <h6 className="fw-semibold mb-3">{STEP_LABELS[step]}</h6>

        {step === 0 && (
          <Step1
            totalIncome={totalIncome}
            setTotalIncome={setTotalIncome}
            persons={persons}
            setPersons={setPersons}
            ipp={ipp}
          />
        )}
        {step === 1 && (
          <Step2
            ipp={ipp}
            basicData={basicData}
            setBasicData={setBasicData}
            activeTier={activeTier}
            tiers={tiers}
          />
        )}
        {step === 2 && (
          <Step3
            appType={appType}
            setAppType={setAppType}
            invoices={invoices}
            setInvoices={setInvoices}
            events={events}
            setEvents={setEvents}
            total={invoiceTotal}
          />
        )}
        {step === 3 && (
          <Step4
            total={invoiceTotal}
            basicData={basicData}
            activeTier={activeTier}
            result={result}
          />
        )}
        {step === 4 && (
          <Step5 tier={activeTier} appType={appType} />
        )}
      </div>

      <div className="calc-footer">
        <button
          type="button"
          className="calc-btn calc-btn--secondary"
          disabled={step === 0}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          {capitalizeFirstLetter(t('faq.calc.prev'))}
        </button>
        <span className="calc-step-indicator">
          {capitalizeFirstLetter(t('faq.calc.step'))} {step + 1} / {STEP_LABELS.length}
        </span>
        {step < STEP_LABELS.length - 1 ? (
          <button
            type="button"
            className="calc-btn calc-btn--primary"
            onClick={() => setStep((s) => Math.min(STEP_LABELS.length - 1, s + 1))}
          >
            {capitalizeFirstLetter(t('faq.calc.next'))}
            <FontAwesomeIcon icon={faArrowRight} />
          </button>
        ) : (
          <button
            type="button"
            className="calc-btn calc-btn--outline"
            onClick={() => setStep(0)}
          >
            {capitalizeFirstLetter(t('faq.calc.step1_title'))}
          </button>
        )}
      </div>
    </div>
  );
};

// ─── FAQ Section definitions ──────────────────────────────────────────────────

interface FaqItem {
  question: string;
  answer: React.ReactNode;
  tags?: string[];
}

interface FaqSection {
  key: string;
  icon: typeof faHeartPulse;
  items: FaqItem[];
}

// ─── Main Component ────────────────────────────────────────────────────────────

const TABS = [
  'all', 'account', 'documents', 'household', 'health', 'education', 'status', 'calculation',
] as const;
type TabKey = typeof TABS[number];

const TAB_ICONS: Record<TabKey, typeof faHeartPulse> = {
  all:         faLayerGroup,
  account:     faLock,
  documents:   faFileLines,
  household:   faHouse,
  health:      faHeartPulse,
  education:   faGraduationCap,
  status:      faLayerGroup,
  calculation: faCoins,
};

const FaqQuestions: React.FC = () => {
  const { t } = useTranslation();
  const [search, setSearch]   = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('all');

  // Build FAQ data dynamically from translation keys
  const faqData = useMemo<Record<Exclude<TabKey, 'all'>, FaqItem[]>>(() => {
    const accountItems: FaqItem[] = [
      {
        question: t('faq.account.q1'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.account.a1'))}</p>
            <FaqNote>{capitalizeFirstLetter(t('faq.account.note_a1'))}</FaqNote>
          </div>
        ),
        tags: ['login', 'account'],
      },
      {
        question: t('faq.account.q2'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.account.a2_intro'))}</p>
            <ul>
              <li>{capitalizeFirstLetter(t('faq.account.a2_r1'))}</li>
              <li>{capitalizeFirstLetter(t('faq.account.a2_r2'))}</li>
            </ul>
            <FaqNote>{capitalizeFirstLetter(t('faq.account.a2_note'))}</FaqNote>
          </div>
        ),
        tags: ['password', 'reset'],
      },
      {
        question: t('faq.account.q3'),
        answer: <p>{capitalizeFirstLetter(t('faq.account.a3'))}</p>,
        tags: ['profile'],
      },
      {
        question: t('faq.account.q4'),
        answer: <p>{capitalizeFirstLetter(t('faq.account.a4'))}</p>,
        tags: ['language'],
      },
      {
        question: t('faq.account.q5'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.account.a5'))}</p>
            <FaqNote type="warning">{capitalizeFirstLetter(t('faq.account.note_a5'))}</FaqNote>
          </div>
        ),
        tags: ['security'],
      },
    ];

    const documentItems: FaqItem[] = [
      {
        question: t('faq.documents.q1'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.documents.a1'))}</p>
            <FaqNote>{capitalizeFirstLetter(t('faq.documents.note_a1'))}</FaqNote>
          </div>
        ),
        tags: ['upload', 'format'],
      },
      {
        question: t('faq.documents.q2'),
        answer: <p>{capitalizeFirstLetter(t('faq.documents.a2'))}</p>,
        tags: ['size', 'limit'],
      },
      {
        question: t('faq.documents.q3'),
        answer: <p>{capitalizeFirstLetter(t('faq.documents.a3'))}</p>,
        tags: ['validity'],
      },
      {
        question: t('faq.documents.q4'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.documents.a4_intro'))}</p>
            <ul>
              <li><strong>{capitalizeFirstLetter(t('faq.documents.a4_7mo'))}</strong>: {capitalizeFirstLetter(t('faq.documents.a4_7mo_docs'))}</li>
              <li><strong>{capitalizeFirstLetter(t('faq.documents.a4_12mo'))}</strong>: {capitalizeFirstLetter(t('faq.documents.a4_12mo_docs'))}</li>
              <li><strong>{capitalizeFirstLetter(t('faq.documents.a4_24mo'))}</strong>: {capitalizeFirstLetter(t('faq.documents.a4_24mo_docs'))}</li>
            </ul>
          </div>
        ),
        tags: ['expiry', 'renewal'],
      },
      {
        question: t('faq.documents.q5'),
        answer: <p>{capitalizeFirstLetter(t('faq.documents.a5'))}</p>,
        tags: ['replace'],
      },
      {
        question: t('faq.documents.q6'),
        answer: <p>{capitalizeFirstLetter(t('faq.documents.a6'))}</p>,
        tags: ['missing'],
      },
    ];

    const householdItems: FaqItem[] = [
      {
        question: t('faq.household.q1'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.household.a1_with'))}</p>
            <p>{capitalizeFirstLetter(t('faq.household.a1_without'))}</p>
            <FaqNote>{capitalizeFirstLetter(t('faq.household.note_a1'))}</FaqNote>
          </div>
        ),
        tags: ['income', 'household'],
      },
      {
        question: t('faq.household.q2'),
        answer: <p>{capitalizeFirstLetter(t('faq.household.a2'))}</p>,
        tags: ['members'],
      },
      {
        question: t('faq.household.q3'),
        answer: <p>{capitalizeFirstLetter(t('faq.household.a3'))}</p>,
        tags: ['children'],
      },
      {
        question: t('faq.household.q4'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.household.a4'))}</p>
            <FaqNote type="warning">{capitalizeFirstLetter(t('faq.household.note_a4'))}</FaqNote>
          </div>
        ),
        tags: ['change', 'update'],
      },
    ];

    const healthItems: FaqItem[] = [
      {
        question: t('faq.health.q1'),
        answer: <p>{capitalizeFirstLetter(t('faq.health.a1'))}</p>,
        tags: ['health', 'refund'],
      },
      {
        question: t('faq.health.q2'),
        answer: (
          <ul>
            <li>{capitalizeFirstLetter(t('faq.health.a2_r1'))}</li>
            <li>{capitalizeFirstLetter(t('faq.health.a2_r2'))}</li>
            <li>{capitalizeFirstLetter(t('faq.health.a2_r3'))}</li>
          </ul>
        ),
        tags: ['invoice'],
      },
      {
        question: t('faq.health.q3'),
        answer: (
          <div>
            <ul>
              <li>{capitalizeFirstLetter(t('faq.health.a3_r1'))}</li>
            </ul>
            <FaqNote>{capitalizeFirstLetter(t('faq.health.note_a3'))}</FaqNote>
          </div>
        ),
        tags: ['insurance', 'svt'],
      },
      {
        question: t('faq.health.q4'),
        answer: <p>{capitalizeFirstLetter(t('faq.health.a4'))}</p>,
        tags: ['products'],
      },
      {
        question: t('faq.health.q5'),
        answer: (
          <ul>
            <li><strong>{capitalizeFirstLetter(t('faq.health.a5_p1_label'))}</strong>: {capitalizeFirstLetter(t('faq.health.a5_p1_desc'))}</li>
            <li><strong>{capitalizeFirstLetter(t('faq.health.a5_p2_label'))}</strong>: {capitalizeFirstLetter(t('faq.health.a5_p2_desc'))}</li>
            <li><strong>{capitalizeFirstLetter(t('faq.health.a5_p3_label'))}</strong>: {capitalizeFirstLetter(t('faq.health.a5_p3_desc'))}</li>
            <li><strong>{capitalizeFirstLetter(t('faq.health.a5_p4_label'))}</strong>: {capitalizeFirstLetter(t('faq.health.a5_p4_desc'))}</li>
          </ul>
        ),
        tags: ['refund policy'],
      },
      {
        question: t('faq.health.q6'),
        answer: <p>{capitalizeFirstLetter(t('faq.health.a6'))}</p>,
        tags: ['limit'],
      },
    ];

    const educationItems: FaqItem[] = [
      {
        question: t('faq.education.q1'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.education.a1'))}</p>
            <FaqNote>{capitalizeFirstLetter(t('faq.education.note_a1'))}</FaqNote>
          </div>
        ),
        tags: ['education', 'school'],
      },
      {
        question: t('faq.education.q2'),
        answer: (
          <ul>
            <li>{capitalizeFirstLetter(t('faq.education.a2_r1'))}</li>
            <li>{capitalizeFirstLetter(t('faq.education.a2_r2'))}</li>
            <li>{capitalizeFirstLetter(t('faq.education.a2_r3'))}</li>
          </ul>
        ),
        tags: ['event', 'activity'],
      },
      {
        question: t('faq.education.q3'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.education.a3_intro'))}</p>
            <ul>
              <li>{capitalizeFirstLetter(t('faq.education.a3_r1'))}</li>
              <li>{capitalizeFirstLetter(t('faq.education.a3_r2'))}</li>
              <li>{capitalizeFirstLetter(t('faq.education.a3_r3'))}</li>
              <li>{capitalizeFirstLetter(t('faq.education.a3_r4'))}</li>
            </ul>
            <p className="text-muted fs-13">{capitalizeFirstLetter(t('faq.education.a3_optional'))}</p>
          </div>
        ),
        tags: ['template'],
      },
      {
        question: t('faq.education.q4'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.education.a4_intro'))}</p>
            <ul>
              <li>{capitalizeFirstLetter(t('faq.education.a4_r1'))}</li>
              <li>{capitalizeFirstLetter(t('faq.education.a4_r2'))}</li>
              <li>{capitalizeFirstLetter(t('faq.education.a4_r3'))}</li>
            </ul>
            <FaqNote>{capitalizeFirstLetter(t('faq.education.a4_note'))}</FaqNote>
          </div>
        ),
        tags: ['payment'],
      },
      {
        question: t('faq.education.q5'),
        answer: (
          <div>
            <p>{capitalizeFirstLetter(t('faq.education.a5'))}</p>
            <FaqNote>{capitalizeFirstLetter(t('faq.education.note_a5'))}</FaqNote>
          </div>
        ),
        tags: ['limit'],
      },
      {
        question: t('faq.education.q6'),
        answer: <p>{capitalizeFirstLetter(t('faq.education.a6'))}</p>,
        tags: ['refund'],
      },
    ];

    const statusItems: FaqItem[] = [
      {
        question: t('faq.status.q1'),
        answer: (
          <Table bordered size="sm" className="fs-13">
            <thead className="table-light">
              <tr>
                <th>{capitalizeFirstLetter(t('faq.status.status_meaning'))}</th>
                <th>{capitalizeFirstLetter(t('faq.status.status_editable'))}</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['status_predraft', 'status_predraft_desc', 'yes'],
                ['status_pending', 'status_pending_desc', 'no'],
                ['status_underreview', 'status_underreview_desc', 'no'],
                ['status_feedback', 'status_feedback_desc', 'partial'],
                ['status_approved', 'status_approved_desc', 'no'],
                ['status_rejected', 'status_rejected_desc', 'no'],
              ].map(([key, desc, editable]) => (
                <tr key={key}>
                  <td>
                    <strong>{capitalizeFirstLetter(t(`faq.status.${key}`))}</strong>
                    <br /><small className="text-muted">{capitalizeFirstLetter(t(`faq.status.${desc}`))}</small>
                  </td>
                  <td className="text-center">
                    {editable === 'yes' ? (
                      <FontAwesomeIcon icon={faCheck} className="text-success" />
                    ) : editable === 'no' ? (
                      <FontAwesomeIcon icon={faXmark} className="text-danger" />
                    ) : (
                      <FontAwesomeIcon icon={faCircleInfo} className="text-warning" />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ),
        tags: ['status', 'overview'],
      },
      {
        question: t('faq.status.q2'),
        answer: (
          <ul>
            <li>{capitalizeFirstLetter(t('faq.status.a2_r1'))}</li>
            <li>{capitalizeFirstLetter(t('faq.status.a2_r2'))}</li>
          </ul>
        ),
        tags: ['pending'],
      },
      { question: t('faq.status.q3'), answer: <p>{capitalizeFirstLetter(t('faq.status.a3'))}</p>, tags: ['underreview'] },
      { question: t('faq.status.q4'), answer: <p>{capitalizeFirstLetter(t('faq.status.a4'))}</p>, tags: ['correction', 'edit'] },
      { question: t('faq.status.q5'), answer: <p>{capitalizeFirstLetter(t('faq.status.a5'))}</p>, tags: ['approved'] },
      { question: t('faq.status.q6'), answer: <p>{capitalizeFirstLetter(t('faq.status.a6'))}</p>, tags: ['rejected'] },
      { question: t('faq.status.q7'), answer: <p>{capitalizeFirstLetter(t('faq.status.a7'))}</p>, tags: ['timeline'] },
    ];

    const calcItems: FaqItem[] = [
      {
        question: t('faq.calc.title'),
        answer: <RefundCalculator />,
        tags: ['calculator', 'refund', 'income'],
      },
    ];

    return {
      account:     accountItems,
      documents:   documentItems,
      household:   householdItems,
      health:      healthItems,
      education:   educationItems,
      status:      statusItems,
      calculation: calcItems,
    };
  }, [t]);

  const filteredItems = useMemo(() => {
    const tabItems = activeTab === 'all'
      ? (Object.keys(faqData) as Exclude<TabKey, 'all'>[]).flatMap((k) => faqData[k])
      : faqData[activeTab as Exclude<TabKey, 'all'>];
    const q = search.toLowerCase().trim();
    if (!q) return tabItems;
    return tabItems.filter(
      (item) =>
        item.question.toLowerCase().includes(q) ||
        (item.tags || []).some((tag) => tag.includes(q))
    );
  }, [search, activeTab, faqData]);

  return (
    <div className="faq-standalone-wrap">
    <Card className="faq-page p-4">
      {/* ── Header card ── */}
      <div className="faq-header-card">
        <div>
          <h2 className="faq-header-card__title">{capitalizeFirstLetter(t('faq.title'))}</h2>
          <p className="faq-header-card__subtitle">{capitalizeFirstLetter(t('faq.subtitle'))}</p>
        </div>
        <div className="faq-header-card__search">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="faq-header-card__search-icon" />
          <input
            placeholder={capitalizeFirstLetter(t('faq.search_placeholder'))}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="btn p-0 border-0 bg-transparent text-muted" onClick={() => setSearch('')} type="button">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
      </div>

      {/* ── Underline tabs ── */}
      <div className="faq-tabs">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            className={`faq-tab${activeTab === tab ? ' faq-tab--active' : ''}`}
            onClick={() => { setActiveTab(tab); setSearch(''); }}
          >
            {capitalizeFirstLetter(t(`faq.tab_${tab}`))}
          </button>
        ))}
      </div>

      {/* ── Accordion ── */}
      {filteredItems.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="fs-30 mb-3 d-block mx-auto" />
          {capitalizeFirstLetter(t('faq.no_results'))}
        </div>
      ) : (
        <Accordion className="faq-accordion-list">
          {filteredItems.map((item, idx) => (
            <Accordion.Item key={idx} eventKey={String(idx)}>
              <Accordion.Header>{item.question}</Accordion.Header>
              <Accordion.Body>{item.answer}</Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}
    </Card>
    </div>
  );
};

export default FaqQuestions;
